import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError} from "../api/client";
import { apiGet, apiPost } from "../api/client";
import { OutputRender } from "../components/review/OutputRender";
import { Modal } from "../components/review/Modal";

interface Attachment {
  id: string;
  kind: "url" | "file";
  url: string | null;
  filename: string | null;
}

interface UpstreamStep {
  id: string;
  ord: number;
  type: string;
  title: string;
  state: string;
  output: unknown;
  successCriteria: string;
  attachments: Attachment[];
}

interface CommentRow {
  id: string;
  authorUserId: string;
  authorLabel: string | null;
  body: string;
  decision: "approve" | "return" | "note" | null;
  createdAt: string;
  attachments: Attachment[];
}

interface ReviewView {
  token: { value: string; expiresAt: string };
  workspace: { id: string };
  play: { id: string };
  step: {
    id: string;
    title: string;
    description: string;
    type: string;
    state: string;
    successCriteria: string;
    output: unknown;
    displayOwner: string | null;
  };
  card: {
    id: string;
    type: string;
    state: string;
    body: Record<string, unknown>;
    allowedActions: Array<{
      action: string;
      decision: "approve" | "return" | "note";
      label: string;
      requiresComment: boolean;
    }>;
  };
  upstream: UpstreamStep[];
  attachments: Attachment[];
  comments: CommentRow[];
}

function Attachments({ items }: { items: Attachment[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="review-attach-list">
      {items.map((a) => (
        <li key={a.id}>
          📎{" "}
          {a.url ? (
            <a href={a.url} target="_blank" rel="noopener noreferrer">
              {a.filename || hostnameOf(a.url) || "link"}
            </a>
          ) : (
            a.filename || "(file)"
          )}
        </li>
      ))}
    </ul>
  );
}
function hostnameOf(url: string): string | null {
  try { return new URL(url).hostname; } catch { return null; }
}

export function ReviewPage() {
  const { token } = useParams<{ token: string }>();
  const qc = useQueryClient();
  const [comment, setComment] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentLabel, setAttachmentLabel] = useState("");
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<null | { label: string }>(null);
  const [commentError, setCommentError] = useState<string | null>(null);

  const reviewQ = useQuery<ReviewView, ApiError>({
    queryKey: ["review", token],
    queryFn: () => apiGet<ReviewView>(`/api/r/${token}`),
    enabled: !!token,
    retry: false,
  });

  const submitM = useMutation<
    unknown,
    ApiError,
    { action: string; comment?: string; attachments?: Array<{ kind: "url"; url: string; filename?: string }> }
  >({
    mutationFn: (body) => apiPost(`/api/r/${token}/action`, body),
    onSuccess: async (_data, vars) => {
      const preset = reviewQ.data?.card.allowedActions.find((a) => a.action === vars.action);
      setSubmitted({ label: preset?.label ?? vars.action });
      await qc.invalidateQueries({ queryKey: ["review", token] });
    },
    onSettled: () => setPendingAction(null),
  });

  const view = reviewQ.data;

  const pendingAttachments = useMemo(() => {
    const u = attachmentUrl.trim();
    if (!u) return [];
    return [{ kind: "url" as const, url: u, ...(attachmentLabel.trim() && { filename: attachmentLabel.trim() }) }];
  }, [attachmentUrl, attachmentLabel]);

  if (!token) {
    return <div className="review-page"><div className="review-banner error">Missing review link.</div></div>;
  }
  if (reviewQ.isLoading) {
    return <div className="review-page">Loading review…</div>;
  }
  if (reviewQ.error) {
    const s = reviewQ.error.status;
    return (
      <div className="review-page">
        <div className={`review-banner ${s === 410 ? "warn" : "error"}`}>
          {s === 404 ? "This review link is invalid or has been revoked."
            : s === 410 ? "This review link has expired. Please request a fresh notification."
            : `Could not load review (${s}).`}
        </div>
      </div>
    );
  }
  if (!view) return null;

  const cardClosed = view.card.state !== "open";
  const tokenUsed = submitted !== null;
  const cardSummary =
    (view.card.body["output_summary"] as string | undefined) ??
    (view.card.body["context"] as string | undefined) ??
    (view.card.body["proposed_brief"] as string | undefined);

  function onAction(actionKey: string) {
    if (!view) return;
    const preset = view.card.allowedActions.find((a) => a.action === actionKey);
    if (!preset) return;
    if (preset.requiresComment && comment.trim().length === 0) {
      setCommentError(
        `A comment is required to ${preset.label.toLowerCase()}.`,
      );
      return;
    }
    setCommentError(null);
    setPendingAction(actionKey);
    submitM.mutate({
      action: actionKey,
      ...(comment.trim() && { comment: comment.trim() }),
      ...(pendingAttachments.length > 0 && { attachments: pendingAttachments }),
    });
  }

  return (
    <main style={{ minHeight: "100vh", background: "var(--bg-canvas, #f8fafc)" }}>
      <div className="review-page" style={{ margin: "0 auto" }}>
        <p className="review-eyebrow">{prettyType(view.card.type)}</p>
        <h1 className="review-title">{view.step.title}</h1>

        {(view.step.successCriteria || cardSummary) && (
          <div className="review-meta">
            {cardSummary && (
              <div style={{ marginBottom: view.step.successCriteria ? 6 : 0 }}>{cardSummary}</div>
            )}
            {view.step.successCriteria && (
              <div><strong>Success criteria:</strong> {view.step.successCriteria}</div>
            )}
          </div>
        )}

        <Modal
          open={tokenUsed}
          tone="ok"
          title={`Submitted: ${submitted?.label ?? ""}`}
          body="Your decision was recorded. You can close this page."
          primaryLabel="Done"
          onPrimary={() => {/* keep banner visible; modal just closes */}}
          onClose={() => {/* allow close */}}
        />

        {!tokenUsed && cardClosed && (
          <div className="review-banner warn">
            This step has been resolved by another reviewer.
          </div>
        )}

        <Modal
          open={
            !!submitM.error &&
            (submitM.error.status === 409 || submitM.error.status === 410)
          }
          tone="warn"
          title={
            submitM.error?.status === 410
              ? "This review link expired"
              : "Already actioned"
          }
          body={
            submitM.error?.status === 410
              ? "Please request a fresh notification from PlayStudio."
              : "Someone else has already responded to this review."
          }
          primaryLabel="Close"
          onPrimary={() => submitM.reset()}
        />

        {view.upstream.length > 0 && (
          <section className="review-section">
            <h2 className="review-section-title">What got us here</h2>
            <div className="review-upstream">
              {view.upstream.map((u) => (
                <details key={u.id} open={view.upstream.length <= 2}>
                  <summary>
                    <strong>{u.title}</strong>
                    <span className="review-upstream-meta">
                      {prettyType(u.type)} · {u.state.replace(/_/g, " ")}
                    </span>
                  </summary>
                  <div className="review-upstream-body">
                    <OutputRender output={u.output} />
                    <Attachments items={u.attachments} />
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {(view.step.output != null || view.attachments.length > 0) && (
          <section className="review-section">
            <h2 className="review-section-title">This step's output</h2>
            <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 14, background: "var(--bg, #fff)" }}>
              <OutputRender output={view.step.output} />
              <Attachments items={view.attachments} />
            </div>
          </section>
        )}

        {view.comments.length > 0 && (
          <section className="review-section">
            <h2 className="review-section-title">Prior comments</h2>
            <ul className="review-comment-list">
              {view.comments.map((c) => (
                <li key={c.id} className="review-comment">
                  <div className="review-comment-head">
                    <span className="author">{c.authorLabel ?? c.authorUserId}</span>
                    {c.decision && (
                      <span className={`review-comment-decision ${c.decision}`}>
                        {c.decision === "approve" ? "approved"
                          : c.decision === "return" ? "requested changes"
                          : "note"}
                      </span>
                    )}
                    <span>· {formatTimestamp(c.createdAt)}</span>
                  </div>
                  <p className="review-comment-body">{c.body}</p>
                  <Attachments items={c.attachments} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {!tokenUsed && !cardClosed && (
          <section className="review-form">
            <h2 className="review-section-title" style={{ marginBottom: 14 }}>Your response</h2>

            <label className="review-field">
              <span className="review-field-label">
                <strong>Comment</strong>
                <span className="review-field-hint">required when requesting changes or rejecting</span>
              </span>
              <textarea
                className="review-textarea"
                rows={4}
                placeholder="Add context, concerns, or sign-off rationale…"
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  if (commentError && e.target.value.trim().length > 0) {
                    setCommentError(null);
                  }
                }}
                aria-invalid={!!commentError}
                style={commentError ? { borderColor: "#dc2626" } : undefined}
              />
              {commentError && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#b91c1c" }}>
                  {commentError}
                </div>
              )}
            </label>

            <div className="review-attach-row" style={{ marginTop: 12 }}>
              <label className="review-field" style={{ marginTop: 0 }}>
                <span className="review-field-label">
                  <strong>Attachment URL</strong>
                  <span className="review-field-hint">optional — Drive, Dropbox, etc.</span>
                </span>
                <input
                  className="review-input"
                  type="url"
                  placeholder="https://drive.google.com/…"
                  value={attachmentUrl}
                  onChange={(e) => setAttachmentUrl(e.target.value)}
                />
              </label>
              <label className="review-field" style={{ marginTop: 0 }}>
                <span className="review-field-label"><strong>Label</strong></span>
                <input
                  className="review-input"
                  type="text"
                  placeholder="optional"
                  value={attachmentLabel}
                  onChange={(e) => setAttachmentLabel(e.target.value)}
                />
              </label>
            </div>

            {submitM.error && submitM.error.status !== 409 && submitM.error.status !== 410 && (
              <div className="review-banner error">Could not submit ({submitM.error.status}).</div>
            )}

            <div className="review-actions">
              {view.card.allowedActions.map((a) => {
                const pending = pendingAction === a.action;
                return (
                  <button
                    key={a.action}
                    type="button"
                    disabled={submitM.isPending}
                    onClick={() => onAction(a.action)}
                    className={`review-btn ${a.decision}`}
                  >
                    {pending ? "Submitting…" : a.label}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <footer style={{ marginTop: 32, fontSize: 12, color: "var(--text-3, #94a3b8)" }}>
          Link expires {formatTimestamp(view.token.expiresAt)}.
        </footer>
      </div>
    </main>
  );
}

function prettyType(t: string): string {
  return ({
    decision: "Decision",
    review: "Review",
    confirmation: "Confirmation",
    intervention: "Intervention",
    milestone: "Milestone",
    query: "Query",
    comm: "Communication",
    micro_task: "Task",
    wait: "Wait",
    instrumentation: "Instrumentation",
  } as Record<string, string>)[t] ?? t;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
